import * as YAML from 'yaml';

import {
  arraySize,
  isArray,
  isObject,
  isPrimitive,
  isUndefined,
  objectSize,
  Serializable,
  SerializableArray,
  SerializableObject,
  SerializablePrimitive,
} from '~/domain/helpers/serializable';
import { YAML_POLICY_STRINGIFY_OPTS } from '~/domain/misc';

export function findLineNumbersToHighlightYaml(
  content: string,
  search: string,
  ignoreKeys = new Set<string>(),
): number[] /* 0-based */ {
  try {
    content = content.replace(/\- \{\}/gi, '- __@cimulator_empty_object@__');
    search = search.replace(/\- \{\}/gi, '- __@cimulator_empty_object@__');

    const intersected = intersect(
      YAML.parse(prependLineNumbers(content)) as Serializable,
      YAML.parse(search) as Serializable,
    );

    if (isUndefined(intersected)) return [];

    const yaml = YAML.stringify(intersected, YAML_POLICY_STRINGIFY_OPTS);

    return grepLineNumbers(yaml, ignoreKeys);
  } catch (error) {
    console.error(error);
    return [];
  }
}

function intersect(a: Serializable, b: Serializable): Serializable | undefined {
  if (isUndefined(a) || isUndefined(b)) return undefined;

  if (isPrimitive(a) && isPrimitive(b)) {
    if (popLineNumber(a).line === b) return a;
    return undefined;
  }

  if (isObject(a)) {
    if (isObject(b) && objectSize(a) === 0 && objectSize(b) === 0) return {};

    let obj: SerializableObject | undefined = undefined;
    outer: for (const [aKey, aVal] of Object.entries(a)) {
      if (isObject(b)) {
        const bKey = popLineNumber(aKey).line;
        if (bKey in b) {
          obj = intersectObject(obj, [aKey, aVal], b[bKey]);
          continue outer;
        }
      }
      obj = intersectObject(obj, [aKey, aVal], b);
    }

    if (obj && isObject(b)) {
      return objectSize(obj) === objectSize(b) ? obj : undefined;
    }

    return obj;
  }

  if (isArray(a)) {
    if (isArray(b) && arraySize(a) === 0 && arraySize(b) === 0) return [];

    let arr: SerializableArray | undefined = undefined;
    outer: for (const aVal of a) {
      if (isArray(b)) {
        for (const bVal of b) arr = intersectArray(arr, aVal, bVal);
        continue outer;
      }
      arr = intersectArray(arr, aVal, b);
    }

    if (arr && isArray(b)) {
      return arraySize(arr) === arraySize(b) ? arr : undefined;
    }

    return arr;
  }

  return undefined;
}

function intersectObject(
  target: SerializableObject | undefined,
  [aKey, aVal]: [string, Serializable],
  bVal: Serializable,
): SerializableObject | undefined {
  const res = intersect(aVal, bVal);
  if (isUndefined(res)) return target;
  return { ...target, [aKey]: res };
}

function intersectArray(
  target: SerializableArray | undefined,
  aVal: Serializable,
  bVal: Serializable,
): SerializableArray | undefined {
  const res = intersect(aVal, bVal);
  if (isUndefined(res)) return target;
  return target ? [res, ...target] : [res];
}

function prependLineNumbers(lines: string): string {
  return lines
    .split('\n')
    .map((line, num) => {
      const n = line.length;
      for (let i = 0; i < n; i++) {
        const ch = line[i];
        if (!/\s/.test(ch) && ch !== '-') {
          if (ch === "'" || ch === '#') {
            return line.slice(0, i + 1) + `${num}_` + line.slice(i + 1);
          } else {
            return line.slice(0, i) + `${num}_` + line.slice(i);
          }
        }
      }
      return '';
    })
    .join('\n');
}

function popLineNumber<T extends SerializablePrimitive>(
  line: T,
): { num: number; line: T } {
  if (typeof line !== 'string') {
    return { num: -1, line };
  }

  const n = line.length;

  if (n === 0) {
    return { num: -1, line };
  }

  for (let i = 0, start = 0; i < n; i++) {
    const ch = line[i];
    if (/\s/.test(ch) || ch === '-') {
      continue;
    }
    if (/^\d+$/.test(ch)) {
      start = i++;
      while (i < n) {
        if (line[i] === '_') {
          return {
            num: +line.slice(start, i),
            line: (line.slice(0, start) + line.slice(i + 1)) as T,
          };
        }
        i++;
      }
    } else if ((ch === "'" || ch === '#') && /^\d+$/.test(line[i + 1])) {
      continue;
    } else {
      break;
    }
  }

  return { num: -1, line };
}

function grepLineNumbers(
  lines: string,
  ignoreKeys = new Set<string>(),
): number[] {
  const lineNumbers = new Set<number>();

  lines
    .split('\n')
    .map(l => {
      const { num, line } = popLineNumber(l);
      const trimmed = line.trim();
      if (trimmed[trimmed.length - 1] === ':') {
        const key = trimmed.slice(0, trimmed.length - 1);
        if (ignoreKeys.has(key.trim())) return -1;
      }
      return num;
    })
    .filter(num => num >= 0)
    .forEach(num => lineNumbers.add(num));

  return Array.from(lineNumbers).sort((a, b) => a - b);
}
